import { useTranslation } from 'react-i18next'
import Button from '../../../components/ui/Button'
import { formatDate } from '../../../lib/datetime'
import { ACTION_STATUS, remainingDays } from '../types'

interface Props {
  deadline: string | null
  statusId: number | null
  /**
   * 087 — Termin güncelleme talebi butonu gösterilsin mi. **Sunucu hesaplar** (termin geçmemiş ·
   * statü uygun · bekleyen talep yok); istemci koşulu yeniden hesaplaMAZ. Verilmezse buton çıkmaz
   * (talep akışı olmayan bağlamlarda kart eskisi gibi davranır).
   */
  requestAllowed?: boolean
  /** 087 — Bekleyen talep varsa id'si; dolu ise buton yerine "onay bekliyor" bilgisi gösterilir. */
  pendingRequestId?: number | null
  /** 087 — Talep pop-up'ını açar. Verilmezse buton hiç render edilmez. */
  onRequestDeadlineUpdate?: () => void
}

/** Statü → termin kartı arka plan sınıfı (analiz 7.5 renk eşlemesi). */
const CARD_CLS: Record<number, string> = {
  [ACTION_STATUS.PENDING]: 'act-termin--pending',
  [ACTION_STATUS.IN_PROGRESS]: 'act-termin--updated',
  [ACTION_STATUS.COMPLETED]: 'act-termin--completed',
  [ACTION_STATUS.CLOSED]: 'act-termin--cancelled',
  [ACTION_STATUS.ARCHIVED]: 'act-termin--cancelled',
}

/**
 * Termin Bilgi Kartı (analiz 7.5): kalan gün + termin tarihi; statüye göre renklendirilir
 * (Bekleniyor mavi · Güncellendi yeşil · Geciken kırmızı · Tamamlandı koyu yeşil · İptal gri).
 *
 * <p>087 ile termin tarihinin **yanına** bir "Termin Güncelleme Talebi" butonu eklendi. Buton ve
 * "onay bekliyor" bilgisi tamamen sunucudan gelen iki alana bağlıdır; kart kendi başına hiçbir
 * uygunluk kuralı değerlendirmez.
 */
export default function ActionTerminCard({
  deadline,
  statusId,
  requestAllowed = false,
  pendingRequestId = null,
  onRequestDeadlineUpdate,
}: Readonly<Props>) {
  const { t } = useTranslation()
  // Tamamlandı / İptal / Güncellendi → kalan gün anlamsız (remainingDays null döner); yalnız termin tarihi gösterilir.
  const rem = remainingDays(deadline, statusId)
  // Termin geçtiyse ("... Gün Geçti") kart zemini statüden bağımsız KIRMIZI (Geciken rengi) olur.
  const overdue = rem != null && rem < 0
  const cls = overdue
    ? 'act-termin--delayed'
    : statusId != null
      ? (CARD_CLS[statusId] ?? '')
      : ''

  const headline = (() => {
    if (rem == null) return t('actions.detail.termin.noneOrDone')
    if (rem > 0) return t('actions.detail.termin.remaining', { count: rem })
    if (rem === 0) return t('actions.detail.termin.today')
    return t('actions.detail.termin.overdue', { count: Math.abs(rem) })
  })()

  return (
    <div className={`act-termin-card ${cls}`}>
      <div className="act-termin-headline">{headline}</div>
      <div className="act-termin-sub">
        <span className="act-termin-label">{t('actions.form.deadline')}</span>
        <span className="act-termin-date">{deadline ? formatDate(deadline) : '—'}</span>
        {/*
          Bekleyen talep varsa buton yerine bilgi durur: aynı aksiyona ikinci talep girilemez (FR-008)
          ve kullanıcıya basamayacağı bir buton göstermenin faydası yok.
        */}
        {pendingRequestId != null ? (
          <span className="act-termin-pending">{t('deadlineRequests.card.pending')}</span>
        ) : (
          requestAllowed &&
          onRequestDeadlineUpdate && (
            <Button
              label={t('deadlineRequests.card.request')}
              size="small"
              variant="secondary"
              onClick={onRequestDeadlineUpdate}
              className="act-termin-request-btn"
            />
          )
        )}
      </div>
    </div>
  )
}
